import { BookOpen, Compass, Brain } from "lucide-react";

type Step = {
  n: string;
  icon: typeof BookOpen;
  label: string;
  title: string;
  body: string;
  accent: string;
  dot: string;
  ringText: string;
};

const steps: Step[] = [
  {
    n: "01",
    icon: BookOpen,
    label: "Learn",
    title: "A short lesson sets the concept.",
    body: "Plain language. Cinematic delivery. No lectures, no worksheets.",
    accent: "from-cyan-400/30 to-cyan-400/0",
    dot: "bg-cyan-400",
    ringText: "text-cyan-200/90",
  },
  {
    n: "02",
    icon: Compass,
    label: "Decide",
    title: "The learner faces a realistic choice.",
    body: "A Decision Check tied to the lesson — friends, money pressure, tradeoffs.",
    accent: "from-indigo-400/35 to-indigo-400/0",
    dot: "bg-indigo-400",
    ringText: "text-indigo-200/90",
  },
  {
    n: "03",
    icon: Brain,
    label: "Reflect",
    title: "Lumi and parent insight turn it into growth.",
    body: "Clear feedback for the teen. Evidence-safe progress insight parents can act on.",
    accent: "from-violet-400/35 to-violet-400/0",
    dot: "bg-violet-400",
    ringText: "text-violet-200/90",
  },
];

const HowItWorksSection = () => (
  <section className="relative py-16 sm:py-20 overflow-hidden">
    <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[680px] w-[680px] rounded-full bg-[radial-gradient(circle,rgba(99,102,241,0.16),transparent_60%)] blur-3xl" />

    <div className="container mx-auto px-6 max-w-6xl relative">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-violet-300/90 mb-4">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-400 shadow-[0_0_10px_rgba(167,139,250,0.95)]" />
          The HeirWise Loop
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-white leading-[1.05]">
          Learn. Decide.{" "}
          <span className="bg-gradient-to-r from-cyan-200 via-indigo-200 to-violet-200 bg-clip-text text-transparent">
            Reflect.
          </span>
        </h2>
        <p className="mt-5 text-white/70 leading-relaxed">
          Each experience moves from a clear concept to a real-world decision, then into feedback your teen can understand — and insight parents can actually use.
        </p>
      </div>

      {/* Connected loop rail */}
      <div className="relative">
        {/* Desktop horizontal trail */}
        <div aria-hidden className="hidden md:block absolute top-[3.75rem] left-[10%] right-[10%] h-px bg-gradient-to-r from-cyan-400/0 via-indigo-400/50 to-violet-400/0" />
        <div aria-hidden className="hidden md:flex absolute top-[3.75rem] left-[10%] right-[10%] -translate-y-1/2 justify-between pointer-events-none">
          {steps.map((s, i) => (
            <span key={i} className={`h-2 w-2 rounded-full ${s.dot} shadow-[0_0_14px_rgba(255,255,255,0.45)]`} />
          ))}
        </div>
        {/* Mobile vertical trail */}
        <div aria-hidden className="md:hidden absolute left-7 top-2 bottom-2 w-px bg-gradient-to-b from-cyan-400/40 via-indigo-400/40 to-violet-400/40" />

        <div className="grid gap-5 md:gap-6 md:grid-cols-3 relative">
          {steps.map(({ n, icon: Icon, label, title, body, accent, ringText, dot }) => (
            <div key={n} className="relative md:pt-0 pl-0 md:pl-0">
              {/* Node */}
              <div className="flex md:flex-col md:items-center items-start gap-4 md:gap-0">
                <div className="relative md:mb-5">
                  <span aria-hidden className={`absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.25),transparent_65%)] blur-md`} />
                  <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#11102a] ring-2 ring-white/15">
                    <span aria-hidden className={`absolute -inset-1 rounded-full bg-[conic-gradient(from_0deg,transparent,rgba(255,255,255,0.35),transparent_60%)] opacity-60 blur-[1px] animate-[spin_18s_linear_infinite]`} />
                    <Icon className="relative h-5 w-5 text-white" />
                    <span className={`absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-full ${dot} ring-2 ring-[#0c0a1f]`} />
                  </span>
                </div>

                <div className="md:text-center flex-1">
                  <div className={`text-[10px] uppercase tracking-[0.3em] ${ringText} flex md:justify-center items-center gap-2`}>
                    <span className="font-heading text-white/55">{n}</span>
                    <span>·</span>
                    <span>{label}</span>
                  </div>
                  <h3 className="mt-2 font-heading text-base sm:text-lg text-white leading-snug">{title}</h3>
                  <p className="mt-1.5 text-sm text-white/65 leading-relaxed md:max-w-[20rem] md:mx-auto">{body}</p>
                </div>
              </div>

              {/* Step glow */}
              <div aria-hidden className={`pointer-events-none absolute -top-10 -z-10 h-40 w-40 rounded-full bg-gradient-to-br ${accent} blur-2xl opacity-60 md:left-1/2 md:-translate-x-1/2 left-0`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default HowItWorksSection;